/**
 * Product Upsell Handlers
 * Global functions for handling product upsell add to cart and variant selection
 */

// Handle variant selection change
window.handleProductUpsellVariantChange = function (selectElement, formId) {
  const form = document.getElementById(formId);
  if (!form) {
    console.error("Form not found:", formId);
    return;
  }

  const productJsonEl = document.getElementById("ProductJson-" + formId);
  if (!productJsonEl) {
    console.error("Product JSON not found for form:", formId);
    return;
  }

  try {
    const product = JSON.parse(productJsonEl.textContent);
    const selects = form.querySelectorAll('select[name^="options["]');
    const selectedOptions = Array.from(selects).map((s) => s.value);

    // Find matching variant
    const variant = product.variants.find((v) => selectedOptions.every((opt, i) => v.options[i] === opt));

    if (variant) {
      const hiddenInput = form.querySelector('input[name="id"]');
      if (hiddenInput) {
        hiddenInput.value = variant.id;
      }

      // Update price if displayed (PDP upsell may show price in column + optional duplicate)
      const currency = document.querySelector('meta[name="currency"]')?.content || "USD";
      const formattedPrice = variant.price ? formatMoney(variant.price, currency) : "";
      form.querySelectorAll(".upsell-price").forEach((el) => {
        if (variant.price) el.textContent = formattedPrice;
      });
    }
  } catch (error) {
    console.error("Error handling variant change:", error);
  }
};

// Handle add to cart
window.handleProductUpsellAdd = async function (buttonElement, formId) {
  const form = document.getElementById(formId);
  if (!form) {
    console.error("Form not found:", formId);
    return;
  }

  const variantId = form.querySelector('input[name="id"]')?.value;
  if (!variantId) {
    console.error("No variant ID found");
    return;
  }

  // Disable button and show loading state
  const originalHTML = buttonElement.innerHTML;
  buttonElement.disabled = true;
  buttonElement.classList.add("is-loading");
  buttonElement.innerHTML = '<span class="button-spinner"></span>';

  try {
    // Add to cart
    const response = await fetch(window.routes.cart_add_url + ".js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to add to cart");
    }

    // Try to parse as JSON, handle errors gracefully
    let data;
    try {
      const text = await response.text();
      data = JSON.parse(text);
    } catch (parseError) {
      console.error("Failed to parse cart response:", parseError);
      throw new Error("Invalid cart response");
    }

    // Show success state
    buttonElement.classList.remove("is-loading");
    buttonElement.classList.add("upsell-add-success");
    buttonElement.innerHTML = "✓ Added!";

    // Reset button after 2 seconds
    setTimeout(() => {
      buttonElement.classList.remove("upsell-add-success");
      buttonElement.innerHTML = originalHTML;
      buttonElement.disabled = false;
    }, 2000);

    // Update cart count bubble
    fetch("/cart.js")
      .then((r) => r.json())
      .then((cart) => {
        document.querySelectorAll(".cart-count-bubble, [data-cart-count]").forEach((el) => {
          el.textContent = cart.item_count;
        });
      })
      .catch(() => {});

    // Refresh cart drawer, then open (open even if section fetch fails)
    const cartDrawer = document.querySelector("cart-drawer");
    const openCartDrawer = () => {
      if (cartDrawer && typeof cartDrawer.open === "function") {
        requestAnimationFrame(() => cartDrawer.open());
      }
    };
    if (cartDrawer) {
      fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const newCartDrawer = doc.querySelector("cart-drawer");
          if (newCartDrawer) {
            cartDrawer.innerHTML = newCartDrawer.innerHTML;
          }
          cartDrawer.dispatchEvent(new CustomEvent("shopify:section:load", { bubbles: true }));
          if (typeof window.scheduleFooterUpsellSliderInit === "function") {
            window.scheduleFooterUpsellSliderInit(cartDrawer);
          } else if (typeof window.initFooterUpsellSlider === "function") {
            window.initFooterUpsellSlider(cartDrawer);
          }
          openCartDrawer();
        })
        .catch(() => openCartDrawer());
    }
  } catch (error) {
    console.error("Error adding to cart:", error);

    // Show error state
    buttonElement.classList.remove("is-loading");
    buttonElement.classList.add("upsell-add-error");
    buttonElement.innerHTML = "✗ Error";

    // Reset button after 2 seconds
    setTimeout(() => {
      buttonElement.classList.remove("upsell-add-error");
      buttonElement.innerHTML = originalHTML;
      buttonElement.disabled = false;
    }, 2000);
  }
};

// Helper function to format money
function formatMoney(cents, currency = "USD") {
  const amount = cents / 100;

  // Try to use Shopify's money format if available
  if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, Shopify.money_format || "{{amount}}");
  }

  // Fallback formatting
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
}

/**
 * Re-inits the footer upsell slider after cart drawer markup changes (upsell fetch, updateCartDrawer,
 * free gift, etc.). Several delayed passes cover async DOM swaps that would otherwise leave arrows inert.
 */
window.scheduleFooterUpsellSliderInit = function scheduleFooterUpsellSliderInit(cartDrawerContainer) {
  if (!cartDrawerContainer || typeof window.initFooterUpsellSlider !== "function") return;
  const el = cartDrawerContainer.matches?.("cart-drawer")
    ? cartDrawerContainer
    : cartDrawerContainer.closest?.("cart-drawer");
  if (!el) return;
  const run = () => window.initFooterUpsellSlider(el);
  run();
  requestAnimationFrame(() => requestAnimationFrame(run));
  setTimeout(run, 0);
  setTimeout(run, 120);
  setTimeout(run, 400);
};

/**
 * Footer upsell: scroll-snap + dots + prev/next arrows (2 products).
 * Uses slide offsetLeft for index (works with 100% or partial-width slides).
 * Re-inits after cart drawer section HTML is replaced (scripts in injected HTML do not run).
 */
window.initFooterUpsellSlider = function initFooterUpsellSlider(scope) {
  const rootScope = scope || document;

  rootScope.querySelectorAll(".gb-cart-drawer-footer-upsell[data-slider]").forEach((root) => {
    const track = root.querySelector(".gb-cart-drawer-footer-upsell__track");
    const slides = Array.from(root.querySelectorAll(".gb-cart-drawer-footer-upsell__slide"));

    // Horizontal scroll is on the track (overflow-x: auto); the viewport wrapper only has overflow: hidden.
    if (!track || slides.length < 2) return;
    if (root.getAttribute("data-slider-bound") === "1") return;

    root.setAttribute("data-slider-bound", "1");

    const dots = root.querySelectorAll(".gb-cart-drawer-footer-upsell__dot");
    const prevArrow = root.querySelector(".gb-cart-drawer-footer-upsell__arrow--prev");
    const nextArrow = root.querySelector(".gb-cart-drawer-footer-upsell__arrow--next");

    const getSlideLeft = (slide) => {
      return slide.offsetLeft - track.offsetLeft;
    };

    const getSlideIndex = () => {
      const scrollLeft = track.scrollLeft;

      let bestIndex = 0;
      let bestDelta = Infinity;

      slides.forEach((slide, index) => {
        const delta = Math.abs(getSlideLeft(slide) - scrollLeft);

        if (delta < bestDelta) {
          bestDelta = delta;
          bestIndex = index;
        }
      });

      return bestIndex;
    };

    const goToSlide = (index) => {
      if (index < 0 || index >= slides.length) return;

      track.scrollTo({
        left: getSlideLeft(slides[index]),
        behavior: "smooth",
      });
    };

    const updateUI = () => {
      const index = getSlideIndex();

      dots.forEach((dot, i) => {
        dot.classList.toggle("is-active", i === index);
        dot.setAttribute("aria-current", i === index ? "true" : "false");
      });

      if (prevArrow) prevArrow.disabled = index === 0;
      if (nextArrow) nextArrow.disabled = index === slides.length - 1;
    };

    let rafId;

    track.addEventListener(
      "scroll",
      () => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(updateUI);
      },
      { passive: true }
    );

    dots.forEach((dot, index) => {
      dot.addEventListener("click", () => goToSlide(index));
    });

    prevArrow?.addEventListener("click", () => {
      goToSlide(getSlideIndex() - 1);
    });

    nextArrow?.addEventListener("click", () => {
      goToSlide(getSlideIndex() + 1);
    });

    updateUI();
  });
};

document.addEventListener("DOMContentLoaded", () => {
  window.initFooterUpsellSlider();
});

document.addEventListener("shopify:section:load", (ev) => {
  if (ev.target?.querySelector?.(".gb-cart-drawer-footer-upsell[data-slider]")) {
    window.initFooterUpsellSlider(ev.target);
  }
});
